/**
 * Local prices that do not apply (360 simulation P33, 11 Oct 2026).
 *
 * The price lookup reads the price data of the case's own company. That price is no
 * price to quote when another Norican company quotes the customer (the entity rule
 * `gate.uk_wgg`: the UK customer is quoted from Wheelabrator Germany) or when the item
 * belongs to another company's catalogue (intercompany: that desk sets the price). The
 * server sends the rule and the catalogue of every line; this file only says which
 * price to grey out and why, in plain words. Pure: no request, no state.
 */

import type { CaseRules } from "@/lib/api";
import type { LinePrice } from "@/lib/mock";

/** The entity rule "this customer is quoted from another Norican company". */
export const QUOTED_ELSEWHERE_RULE = "gate.uk_wgg";

/** The short tag of a greyed price. */
export const NOT_APPLYING_TAG = "Does not apply";

/** True when the case rules say another Norican company quotes this customer. */
export function quotedElsewhere(rules: CaseRules | null | undefined): boolean {
  return (rules?.reasons ?? []).some((reason) => reason?.rule_id === QUOTED_ELSEWHERE_RULE);
}

/**
 * Why the local price of a line is no price to quote, or null when it applies.
 *
 * Only a found price can be greyed out. An item of another catalogue without a price
 * (`other_entity`) gets the intercompany hint instead, so sales know whom to ask.
 */
export function localPriceNote(args: {
  price: Pick<LinePrice, "status"> | null | undefined;
  rules?: CaseRules | null;
  /** The area of the catalogue the line's item comes from; null when unknown. */
  catalogueArea?: string | null;
  /** The area of the case. */
  caseArea?: string | null;
  /** The plain name of an area ("Wheelabrator Germany", "UK"). */
  areaLabel?: (area: string) => string;
}): { greyed: boolean; text: string } | null {
  const { price, rules, catalogueArea, caseArea } = args;
  const label = (area: string) => args.areaLabel?.(area) ?? area.toUpperCase();
  const otherCatalogue = Boolean(
    catalogueArea && caseArea && catalogueArea.toLowerCase() !== caseArea.toLowerCase(),
  );
  if (price?.status === "other_entity") {
    return catalogueArea
      ? {
          greyed: false,
          text: `The item belongs to ${label(catalogueArea)}. Ask that desk for the intercompany price.`,
        }
      : null;
  }
  if (price?.status !== "found") {
    return null;
  }
  if (quotedElsewhere(rules)) {
    return {
      greyed: true,
      text: "Local price, not for this customer: another Norican company quotes it (see the rule above).",
    };
  }
  if (otherCatalogue && catalogueArea) {
    return {
      greyed: true,
      text: `Local price, not for this item: it belongs to ${label(catalogueArea)}. Ask that desk for the intercompany price.`,
    };
  }
  return null;
}

/** The line with its price left out when the local price does not apply (for the totals). */
export function applyingPrice<T extends { price?: LinePrice | null }>(
  line: T,
  note: { greyed: boolean } | null,
): T {
  return note?.greyed ? { ...line, price: null } : line;
}
